import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Menu, MenuItem } from '@material-ui/core';
import { ArrowDropDown as ArrowDropDownIcon } from '@material-ui/icons';

import AppSnackbar from '../../../common/AppSnackbar';
import {
  getExportTitle,
  getProductExportFileName,
  useExportProductEntityDocument,
  useFindProductTemplates
} from '../Product.hooks';
import {
  Product,
  ProductExportEntity,
  ProductTemplate
} from '../Product.interface';
import { ApiParams } from '../../../common/api/api';

export interface ProductExportButtonProps {
  product: Product;
  entity?: ProductExportEntity;
  apiParams?: ApiParams;
  fetchRef?: React.MutableRefObject<Function | undefined>;
}

const ProductExportButton: React.FC<ProductExportButtonProps> = (
  props: ProductExportButtonProps
) => {
  const { product, entity, apiParams, fetchRef } = props;
  const [template, setTemplate] = useState<ProductTemplate>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const {
    productTemplates = [],
    isLoading: isFetching,
    findProductTemplates
  } = useFindProductTemplates(product.id);
  const [{ isLoading, error, response }, getDocument, downloadDocument] =
    useExportProductEntityDocument(product.id, template?.id || 0);
  // sort templates list in the ui based on product templatePosition
  const sortOrder = product.templatePosition?.map(tmp => tmp.id) || [];
  const sortedProductTemplates = productTemplates.sort((a, b) => {
    return sortOrder.indexOf(a.id) - sortOrder.indexOf(b.id);
  });
  const templates = !entity
    ? sortedProductTemplates
    : sortedProductTemplates.filter(template => template.type === entity);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductTemplates;
    }
    findProductTemplates();
  }, [fetchRef, findProductTemplates]);

  useEffect(() => {
    template && getDocument(apiParams);
  }, [template, getDocument]);

  useEffect(() => {
    if (response?.ok && template) {
      const fileName: string = getProductExportFileName(template.name, product);
      downloadDocument(fileName);
      // unset template after download
      setTemplate(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  return (
    <span className='export-button-container'>
      <Button
        color='primary'
        variant='contained'
        className='flex-label'
        disabled={isLoading}
        endIcon={
          isLoading ? (
            <CircularProgress color='primary' size={20} />
          ) : (
            <ArrowDropDownIcon />
          )
        }
        onClick={event => setMenuAnchorEl(event.currentTarget)}
      >
        Export
      </Button>
      <Menu
        id='product-export-menu'
        keepMounted
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
      >
        {templates.length === 0 && (
          <MenuItem disabled>
            {isFetching ? 'Loading options...' : 'No options.'}
          </MenuItem>
        )}
        {templates.map(template => {
          const exportTitle = getExportTitle(template.type);
          const title =
            template.name !== exportTitle
              ? `${template.name} - ${exportTitle}`
              : undefined;
          return (
            <MenuItem
              key={template.id}
              title={title}
              onClick={() => {
                setTemplate(template);
                setMenuAnchorEl(null);
              }}
            >
              {template.name}
            </MenuItem>
          );
        })}
      </Menu>
      <AppSnackbar severity='error' open={!!error}>
        {error?.message || 'An error occurred while exporting the document.'}
      </AppSnackbar>
    </span>
  );
};

export default ProductExportButton;
